import React from "react"

/**
 * Below is an example of using a Provider style pattern for React State using
 * React Hooks. It's strength is more evident in global state needed between
 * components. Proof of concept to consider for either Global state management,
 * ie: Authentiation, or User.
 */

/*
  Contrived example of it below in practice
 */

/*
  <StatusProvider currentStatus={planData.currentStatus}>
    <StatusButton />
    <StatusDisplay />
  </StatusProvider>
*/

/*
  function StatusButton(): JSX.Element {
    const { updateStatus } = useStatus()
    return (
      <select onChange={(event): {} => updateStatus(event)}>
        <option>In Progress</option>
        <option>Done</option>
        <option>On Hold</option>
      </select>
    )
  }

  function StatusDisplay(): JSX.Element {
    const {
      state: { status }
    } = useStatus()
    return <div>The current status is {status}</div>
  }
*/

type StatusPropsInterface = {
  state: { status: string }
  dispatch: React.DispatchWithoutAction
  updateStatus: (e) => {}
}

// create Context with default values
const StatusContext = React.createContext(undefined)

function statusReducer(state, action): {} {
  switch (action.type) {
    case "UPDATE": {
      return { status: action.status }
    }
    default: {
      throw new Error(`Unsupported action type: ${action.type}`)
    }
  }
}

function StatusProvider({
  currentStatus,
  ...props
}: {
  currentStatus: string
  props?: {}
  children: Array<JSX.Element>
}): JSX.Element {
  const [state, dispatch] = React.useReducer(statusReducer, {
    status: currentStatus
  })

  const value = React.useMemo(() => [state, dispatch], [state])

  return <StatusContext.Provider value={value} {...props} />
}

function useStatus(): StatusPropsInterface {
  const statusContext = React.useContext(StatusContext)

  if (!statusContext) {
    throw new Error(`useStatus must be used within a StatusProvider`)
  }

  // destructure `state` and `dispatch` from current StatusContext which the
  // StatusProvider has added via `useReducer`
  const [state, dispatch] = statusContext

  // callback to update status
  const updateStatus = (event: React.ChangeEvent<HTMLInputElement>): {} => {
    return dispatch({ type: "UPDATE", status: event.target.value })
  }

  return {
    state,
    dispatch,
    updateStatus
  }
}

export { StatusProvider, useStatus }
